import { useCallback } from 'react';
import { useGetTenantConfigurationQuery } from 'store/api/tenants';
import { FeatureFlag } from 'types/features';
import { useIsFeatureEnabled } from './useFeatureFlags';

type TimecardPrecision = {
    /** Precision used when showing hours anywhere in the UI (lists, totals, exports). */
    displayDecimalScale: 1 | 2;
    /** Precision the create/edit forms accept; also the precision of values loaded into them. */
    entryDecimalScale: 1 | 2;
    incrementStep: number;
    isTwoDecimalDisplayEnabled: boolean;
    isTwoDecimalEntryEnabled: boolean;
};

export const useTimecardPrecision = (): TimecardPrecision => {
    const isTwoDecimalDisplayEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_TWO_DECIMAL_POINTS_DISPLAYMENT);
    const isTwoDecimalEntryEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_TWO_DECIMAL_POINTS_ENTRIES);
    return {
        displayDecimalScale: isTwoDecimalDisplayEnabled ? 2 : 1,
        entryDecimalScale: isTwoDecimalEntryEnabled ? 2 : 1,
        incrementStep: 0.1, // Keep at 0.1 for usability
        isTwoDecimalDisplayEnabled,
        isTwoDecimalEntryEnabled,
    };
};

const FALLBACK_TIMECARD_INCREMENT = 0.1;

// All calculations below use integer hundredths to avoid float artifacts (e.g. 0.7000000000000001).
const toCents = (value: number): number => Math.round(value * 100);

/** Number of decimals needed to display values sitting on the given increment grid. */
export const getIncrementDecimalScale = (increment: number): 1 | 2 => (toCents(increment) % 10 === 0 ? 1 : 2);

/** Whether the value sits exactly on a multiple of the increment. */
export const isOnIncrementGrid = (value: number, increment: number): boolean =>
    toCents(value) % toCents(increment) === 0;

/** Smallest multiple of the increment that is >= value. Values already on the grid are returned unchanged. */
export const roundUpToIncrement = (value: number, increment: number): number => {
    const incrementCents = toCents(increment);
    return (Math.ceil(toCents(value) / incrementCents) * incrementCents) / 100;
};

/** One increment up when on the grid, otherwise the nearest multiple above. */
export const stepUpByIncrement = (value: number, increment: number): number => {
    const valueCents = toCents(value);
    const incrementCents = toCents(increment);
    const newCents =
        valueCents % incrementCents === 0
            ? valueCents + incrementCents
            : Math.ceil(valueCents / incrementCents) * incrementCents;
    return newCents / 100;
};

/** One increment down when on the grid, otherwise the nearest multiple below. Never goes below zero. */
export const stepDownByIncrement = (value: number, increment: number): number => {
    const valueCents = toCents(value);
    const incrementCents = toCents(increment);
    const newCents =
        valueCents % incrementCents === 0
            ? valueCents - incrementCents
            : Math.floor(valueCents / incrementCents) * incrementCents;
    return Math.max(0, newCents) / 100;
};

type TimecardIncrementResolver = {
    /**
     * Resolves the effective timecard increment for a matter: the matter's own increment,
     * falling back to the tenant default, then to 0.1. Returns undefined when increment
     * control is disabled, meaning the legacy stepping behavior applies.
     */
    resolveTimecardIncrement: (matterTimecardIncrement?: number | null) => number | undefined;
    /** True while the tenant configuration needed for the fallback is still loading. */
    isLoading: boolean;
};

export const useTimecardIncrementResolver = (): TimecardIncrementResolver => {
    const isIncrementControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_INCREMENT_CONTROL);
    const { data: tenantConfiguration, isLoading } = useGetTenantConfigurationQuery(undefined, {
        skip: !isIncrementControlEnabled,
    });
    const defaultTimecardIncrement = tenantConfiguration?.defaultTimecardIncrement;

    const resolveTimecardIncrement = useCallback(
        (matterTimecardIncrement?: number | null): number | undefined => {
            if (!isIncrementControlEnabled) {
                return undefined;
            }
            return matterTimecardIncrement ?? defaultTimecardIncrement ?? FALLBACK_TIMECARD_INCREMENT;
        },
        [isIncrementControlEnabled, defaultTimecardIncrement]
    );

    return {
        resolveTimecardIncrement,
        isLoading: isIncrementControlEnabled && isLoading,
    };
};
